import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { getRemoteConfig, fetchAndActivate, getAll } from 'firebase/remote-config'
import { DEFAULT_REMOTE_CONFIG, RemoteConfigSettings, isProduction } from '../../../config'
import { initializeFirebase } from '../../firebase'
import { FeatureFlagHook } from '../types'

const REFRESH_INTERVAL_MS = 10 * 60 * 1000 // 10 minutes

/**
 * useFirebaseFeatureFlags
 *
 * Hook for Firebase Remote Config-based feature flags (cloud deployments).
 * Automatically refreshes flags every 10 minutes to keep them up-to-date.
 */
export default function useFirebaseFeatureFlags(): FeatureFlagHook {
  const [flags, setFlags] = useState<RemoteConfigSettings>(DEFAULT_REMOTE_CONFIG)
  const [loaded, setLoaded] = useState(false)
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Load flags on mount
  useEffect(() => {
    function startPeriodicRefresh(loadFlagsFn: () => Promise<void>) {
      // Clear any existing interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }

      // Set up new interval to refresh flags
      refreshIntervalRef.current = setInterval(() => {
        setLoaded(false)
        loadFlagsFn().catch((error) => {
          console.error('Failed to refresh Firebase Remote Config:', error)
        })
      }, REFRESH_INTERVAL_MS)
    }

    async function loadFlags() {
      // Ensure Firebase app is initialized before getting remote config
      const app = initializeFirebase()
      const remoteConfig = getRemoteConfig(app)

      if (!remoteConfig) {
        console.warn('Firebase Remote Config not available, using defaults')
        setFlags(DEFAULT_REMOTE_CONFIG)
        setLoaded(true)
        return
      }

      // Configure fetch interval based on environment
      // Production: 1 hour (less frequent to reduce costs/load)
      // Dev/Staging: 10 seconds (allows rapid testing of flag changes)
      remoteConfig.settings.minimumFetchIntervalMillis = isProduction ? 3600000 : 10000
      remoteConfig.defaultConfig = DEFAULT_REMOTE_CONFIG

      try {
        await fetchAndActivate(remoteConfig)
        const remoteFlags = getAll(remoteConfig)

        const newFlags = Object.keys(remoteFlags).reduce((acc, key) => {
          return { ...acc, [key]: remoteFlags[key].asString() }
        }, {} as RemoteConfigSettings)

        setFlags(newFlags)
        setLoaded(true)

        // Start periodic refresh after successful initial load
        startPeriodicRefresh(loadFlags)
      } catch (error) {
        console.error('Failed to fetch Firebase Remote Config, using defaults:', error)
        setFlags(DEFAULT_REMOTE_CONFIG)
        setLoaded(true)
      }
    }

    loadFlags()

    // Cleanup: stop periodic refresh on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }
    }
  }, [])

  const getFlags = useCallback(() => flags, [flags])

  const isLoadedFn = useCallback(() => loaded, [loaded])

  const load = useCallback(async () => {
    // No-op: loading happens automatically in useEffect
    return Promise.resolve()
  }, [])

  const markAsNotLoaded = useCallback(() => {
    setLoaded(false)
  }, [])

  return useMemo(
    () => ({
      getFlags,
      isLoaded: isLoadedFn,
      load,
      markAsNotLoaded,
    }),
    [getFlags, isLoadedFn, load, markAsNotLoaded]
  )
}
